import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import api, { mediaUrl } from "@/lib/api";
import { ZAR } from "@/lib/cart";

export default function SearchAutocomplete() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef();
  const nav = useNavigate();

  useEffect(() => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/products", { params: { search: q, limit: 6 } });
        setResults(data);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    nav(`/shop?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="search-toggle"
        className="inline-flex items-center justify-center h-9 w-9 border border-white/15 hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
      >
        <MagnifyingGlass size={16} weight="bold" />
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-40 w-[92vw] max-w-[420px] border border-white/15 bg-[#0d0d0d]/98 backdrop-blur p-3 shadow-2xl">
          <form onSubmit={submit} className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              autoFocus
              data-testid="search-input"
              placeholder="Search flavours, brands, categories..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full border border-white/15 bg-black py-2.5 pl-9 pr-8 text-sm outline-none focus:border-[#00FFC2]"
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40">
                <X size={14} weight="bold" />
              </button>
            )}
          </form>
          <div className="mt-3 max-h-[60vh] overflow-y-auto">
            {loading && <div className="p-3 text-xs text-white/40">Searching...</div>}
            {!loading && q.length >= 2 && results.length === 0 && (
              <div className="p-3 text-xs text-white/40">No matches for "{q}"</div>
            )}
            {results.map((p) => (
              <Link
                key={p.slug}
                to={`/product/${p.slug}`}
                onClick={() => setOpen(false)}
                data-testid="search-result"
                className="flex items-center gap-3 p-2 hover:bg-white/5"
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden border border-white/10 bg-black">
                  {p.images?.[0] && <img src={mediaUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold">{p.name}</div>
                  <div className="text-[10px] font-label text-white/40">{p.brand} · {p.category}</div>
                </div>
                <div className="text-xs text-[#00FFC2]">{ZAR(p.price)}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
